/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    DATABASE_STATISTICS_PAGE
 * Date:         2026-01-XX
 * Modified By:  Cursor
 *
 * Description:
 * React component for the Database Statistics admin page. Displays dynamically discovered
 * MongoDB collections with document counts and physical sizes. NO hardcoded collection lists.
 *
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Adding: ~180 lines
 * - Projected: ~180 lines
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .h1, .h2, .p
 * - Tokens used: --font-size-*, --line-height-*
 *
 * Related Screens / Modules:
 * - client/src/services/admin/databaseStatisticsService.ts
 * - server/controllers/admin/databaseStatisticsController.js
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Spinner, Alert } from 'react-bootstrap';
import { getDatabaseStatistics, CollectionStat } from '../../services/admin/databaseStatisticsService';

/**
 * Format bytes to human-readable string
 * [2026-01-XX] Size formatting: Convert bytes to B, KB, MB, GB
 */
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format number with thousand separators
 * [2026-01-XX] Number formatting: Add commas for readability
 */
const formatNumber = (num: number): string => {
  if (num < 0) return 'Error';
  return num.toLocaleString();
};

const DatabaseStatisticsPage: React.FC = () => {
  const [collections, setCollections] = useState<CollectionStat[]>([]);
  const [totalCollections, setTotalCollections] = useState<number>(0);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [totalSize, setTotalSize] = useState<number>(0); // Total size in KB
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // [2026-01-XX] Data fetch: Fetch dynamically discovered collections
  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDatabaseStatistics();
      
      if (response.success && response.data) {
        setCollections(response.data.collections);
        setTotalCollections(response.data.totalCollections);
        setTotalDocuments(response.data.totalDocuments);
        setTotalSize(response.data.totalSize);
      } else {
        setError('Failed to load database statistics');
      }
    } catch (err: any) {
      console.error('Failed to fetch database statistics:', err);
      setError(err.message || 'Failed to load database statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  // [2026-01-XX] Data fetch: Load statistics on mount only (no auto-refresh)
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return (
    <div className="admin-page">
      <h2>Database Statistics</h2>

      {/* Summary Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Card.Title>Summary</Card.Title>
          <div className="row">
            <div className="col-md-4">
              <p className="mb-0">
                <strong>Total Collections:</strong> {formatNumber(totalCollections)}
              </p>
            </div>
            <div className="col-md-4">
              <p className="mb-0">
                <strong>Total Documents:</strong> {formatNumber(totalDocuments)}
              </p>
            </div>
            <div className="col-md-4">
              <p className="mb-0">
                <strong>Total Size:</strong> {formatBytes(totalSize * 1024)}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Collections Table */}
      <Card className="shadow-sm">
        <Card.Body>
          <Card.Title>Collections</Card.Title>
          <div className="table-responsive">
            {loading && (
              <div className="text-center">
                <Spinner animation="border" />
                <p className="mt-2">Loading database statistics...</p>
              </div>
            )}
            
            {error && (
              <Alert variant="danger">{error}</Alert>
            )}
            
            {!loading && !error && collections.length === 0 && (
              <Alert variant="info">No collections found.</Alert>
            )}
            
            {!loading && !error && collections.length > 0 && (
              <Table striped bordered hover responsive className="mt-3">
                <thead>
                  <tr>
                    <th>Collection Name</th>
                    <th>Document Count</th>
                    <th>Physical Size</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map((collection) => (
                    <tr key={collection.name}>
                      <td>
                        <code>{collection.name}</code>
                      </td>
                      <td>
                        {collection.count < 0 ? (
                          <span className="text-danger">
                            Error: {collection.error || 'Unable to get count'}
                          </span>
                        ) : (
                          formatNumber(collection.count)
                        )}
                      </td>
                      <td>{formatBytes(collection.size * 1024)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DatabaseStatisticsPage;
